import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './AdminCameraSetup.css';
import {
  listImages, uploadImage, deleteImage, imageUrl,
  getFacilities, createFacility,
  getCameras, createCamera, deleteCamera,
  getCameraZones, createZone, deleteZone,
  runDetection, captureFrame,
} from '../../services/camera.service';

const TABS = ['Images', 'Cameras', 'Zone Editor', 'Run Detection'];

export default function AdminCameraSetup() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('Images');

  // Auth guard — redirect to sign-in if no token
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    if (!user || !user.access_token) {
      navigate('/signin');
    }
  }, [navigate]);

  // ── Images ────────────────────────────────────────────────
  const [images, setImages] = useState([]);
  const [imgLoading, setImgLoading] = useState(false);
  const [imgError, setImgError] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [selectedImages, setSelectedImages] = useState(new Set());

  // ── Cameras ───────────────────────────────────────────────
  const [cameras, setCameras] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [camLoading, setCamLoading] = useState(false);
  const [newCamLocation, setNewCamLocation] = useState('');
  const [newCamFacility, setNewCamFacility] = useState('');
  const [newFacilityName, setNewFacilityName] = useState('');
  const [showNewFacility, setShowNewFacility] = useState(false);

  // ── Zone Editor ───────────────────────────────────────────
  const canvasRef = useRef(null);
  const [editorCamera, setEditorCamera] = useState('');
  const [editorImage, setEditorImage] = useState('');
  const [currentPolygon, setCurrentPolygon] = useState([]);   // [{x,y}, ...]
  const [polygonClosed, setPolygonClosed] = useState(false);  // true when polygon is closed
  const [isExclusion, setIsExclusion] = useState(false);      // true = blackout/exclusion zone
  const [savedZones, setSavedZones] = useState([]);            // [{zone_id, zone_name, polygon, is_exclusion}]
  const [zoneName, setZoneName] = useState('');
  const [zoneMsg, setZoneMsg] = useState('');
  const [imageNaturalSize, setImageNaturalSize] = useState({ w: 1, h: 1 });
  const [displaySize, setDisplaySize] = useState({ w: 800, h: 450 });
  const imgRef = useRef(null);

  // ── Capture from camera working dir ───────────────────────
  const [captureCamera, setCaptureCamera] = useState('');
  const [capturing, setCapturing] = useState(false);
  const [captureResult, setCaptureResult] = useState(null);
  const [captureError, setCaptureError] = useState('');

  // ── Detection ─────────────────────────────────────────────
  const [detCamera, setDetCamera] = useState('');
  const [detImage, setDetImage] = useState('');
  const [detRunning, setDetRunning] = useState(false);
  const [detResult, setDetResult] = useState(null);
  const [detError, setDetError] = useState('');

  // ── Load data ─────────────────────────────────────────────
  useEffect(() => { loadImages(); loadCameras(); }, []);

  async function loadImages() {
    setImgLoading(true);
    setImgError('');
    try { setImages(await listImages()); }
    catch (e) { setImgError(String(e)); }
    finally { setImgLoading(false); }
    setSelectedImages(new Set());
  }

  async function loadCameras() {
    setCamLoading(true);
    try {
      const [cams, facs] = await Promise.all([getCameras(), getFacilities()]);
      setCameras(cams);
      setFacilities(facs);
    } catch (e) { /* silently ignore */ }
    finally { setCamLoading(false); }
  }

  // ── Image upload ──────────────────────────────────────────
  async function handleUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setImgError('');
    try {
      await uploadImage(file);
      await loadImages();
    } catch (err) {
      setImgError(`Upload failed: ${err.message}`);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  }

  function toggleSelectImage(filename) {
    setSelectedImages(prev => {
      const next = new Set(prev);
      next.has(filename) ? next.delete(filename) : next.add(filename);
      return next;
    });
  }

  function selectAllImages() {
    setSelectedImages(new Set(images.map(img => img.filename)));
  }

  function clearSelection() {
    setSelectedImages(new Set());
  }

  async function handleDeleteImage(filename) {
    if (!window.confirm(`Delete ${filename}?`)) return;
    try {
      await deleteImage(filename);
      await loadImages();
    } catch (err) {
      setImgError(`Delete failed: ${err.message}`);
    }
  }

  async function handleDeleteSelected() {
    if (selectedImages.size === 0) return;
    if (!window.confirm(`Delete ${selectedImages.size} image${selectedImages.size > 1 ? 's' : ''}?`)) return;
    setImgError('');
    const failed = [];
    for (const filename of selectedImages) {
      try { await deleteImage(filename); }
      catch { failed.push(filename); }
    }
    if (failed.length > 0) setImgError(`Failed to delete: ${failed.join(', ')}`);
    await loadImages();
  }

  // ── Capture frame from working dir ───────────────────────
  async function handleCaptureFrame() {
    if (!captureCamera) {
      setCaptureError('Select a camera first.');
      return;
    }
    setCapturing(true);
    setCaptureError('');
    setCaptureResult(null);
    try {
      const result = await captureFrame(parseInt(captureCamera));
      setCaptureResult(result);
    } catch (err) {
      setCaptureError(`Capture failed: ${err.message}`);
    } finally {
      await loadImages(); // always refresh — image may have been archived even if detection failed
      setCapturing(false);
    }
  }

  // ── Camera create ─────────────────────────────────────────
  async function handleCreateFacility() {
    if (!newFacilityName.trim()) return;
    try {
      const fac = await createFacility(newFacilityName.trim());
      setFacilities(prev => [...prev, fac]);
      setNewCamFacility(String(fac.id));
      setShowNewFacility(false);
      setNewFacilityName('');
    } catch (err) {
      alert(`Failed to create facility: ${err.message}`);
    }
  }

  async function handleCreateCamera() {
    if (!newCamLocation.trim() || !newCamFacility) {
      alert('Enter a camera location and select a facility.');
      return;
    }
    try {
      await createCamera(newCamLocation.trim(), parseInt(newCamFacility));
      setNewCamLocation('');
      setNewCamFacility('');
      await loadCameras();
    } catch (err) {
      alert(`Failed to create camera: ${err.message}`);
    }
  }

  async function handleDeleteCamera(id) {
    if (!window.confirm('Delete this camera and all its zones?')) return;
    try {
      await deleteCamera(id);
      await loadCameras();
    } catch (err) {
      alert(`Failed: ${err.message}`);
    }
  }

  // ── Zone editor ───────────────────────────────────────────
  async function loadZones(cameraId) {
    try {
      const zones = await getCameraZones(cameraId);
      setSavedZones(zones);
    } catch { setSavedZones([]); }
  }

  useEffect(() => {
    if (editorCamera) loadZones(parseInt(editorCamera));
    else setSavedZones([]);
    setCurrentPolygon([]);
    setPolygonClosed(false);
    setIsExclusion(false);
    setZoneMsg('');
  }, [editorCamera]);

  // Draw canvas whenever polygon, image, or saved zones change
  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const scaleX = canvas.width / imageNaturalSize.w;
    const scaleY = canvas.height / imageNaturalSize.h;

    const countingColors = ['rgba(76,175,80,0.35)', 'rgba(33,150,243,0.35)', 'rgba(255,152,0,0.35)',
                            'rgba(233,30,99,0.35)', 'rgba(156,39,176,0.35)'];
    let countingIdx = 0;

    savedZones.forEach((zone) => {
      const pts = zone.polygon;
      if (!pts || pts.length < 2) return;
      ctx.beginPath();
      ctx.moveTo(pts[0][0] * scaleX, pts[0][1] * scaleY);
      pts.slice(1).forEach(p => ctx.lineTo(p[0] * scaleX, p[1] * scaleY));
      ctx.closePath();

      if (zone.is_exclusion) {
        // Blackout zone — dark red hatched overlay
        ctx.fillStyle = 'rgba(30,0,0,0.55)';
        ctx.fill();
        ctx.strokeStyle = '#cc0000';
        ctx.setLineDash([6, 4]);
        ctx.lineWidth = 2;
        ctx.stroke();
        ctx.setLineDash([]);
        const cx = pts.reduce((s, p) => s + p[0], 0) / pts.length * scaleX;
        const cy = pts.reduce((s, p) => s + p[1], 0) / pts.length * scaleY;
        ctx.font = 'bold 13px sans-serif';
        ctx.fillStyle = '#ff6666';
        ctx.textAlign = 'center';
        ctx.fillText(`🚫 ${zone.zone_name}`, cx, cy);
      } else {
        const color = countingColors[countingIdx % countingColors.length];
        countingIdx++;
        ctx.fillStyle = color;
        ctx.fill();
        ctx.strokeStyle = color.replace('0.35', '1');
        ctx.lineWidth = 2;
        ctx.stroke();
        const cx = pts.reduce((s, p) => s + p[0], 0) / pts.length * scaleX;
        const cy = pts.reduce((s, p) => s + p[1], 0) / pts.length * scaleY;
        ctx.font = 'bold 14px sans-serif';
        ctx.fillStyle = '#fff';
        ctx.textAlign = 'center';
        ctx.fillText(zone.zone_name, cx, cy);
      }
    });

    // Draw current polygon being drawn
    if (currentPolygon.length > 0) {
      const drawColor = isExclusion ? '#cc0000' : '#ff5722';
      ctx.beginPath();
      ctx.moveTo(currentPolygon[0].x, currentPolygon[0].y);
      currentPolygon.slice(1).forEach(p => ctx.lineTo(p.x, p.y));
      if (polygonClosed) {
        ctx.closePath();
        ctx.fillStyle = isExclusion ? 'rgba(180,0,0,0.25)' : 'rgba(255,87,34,0.2)';
        ctx.fill();
      }
      if (isExclusion) ctx.setLineDash([6, 4]);
      ctx.strokeStyle = drawColor;
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.setLineDash([]);
      currentPolygon.forEach((p, i) => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, i === 0 ? 7 : 5, 0, Math.PI * 2);
        ctx.fillStyle = i === 0 ? drawColor : '#fff';
        ctx.fill();
        ctx.strokeStyle = drawColor;
        ctx.lineWidth = 2;
        ctx.stroke();
      });
    }
  }, [currentPolygon, polygonClosed, isExclusion, savedZones, imageNaturalSize]);

  useEffect(() => { drawCanvas(); }, [drawCanvas]);

  function handleImageLoad(e) {
    const img = e.target;
    const natural = { w: img.naturalWidth, h: img.naturalHeight };
    setImageNaturalSize(natural);
    // Fit to max display width
    const maxW = 800;
    const scale = Math.min(1, maxW / natural.w);
    setDisplaySize({ w: Math.round(natural.w * scale), h: Math.round(natural.h * scale) });
  }

  function handleCanvasClick(e) {
    if (polygonClosed) return; // polygon already closed — use Clear to start over
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Close polygon if clicking near the first point (within 15px) and 3+ points placed
    if (currentPolygon.length >= 3) {
      const dx = currentPolygon[0].x - x;
      const dy = currentPolygon[0].y - y;
      if (Math.sqrt(dx * dx + dy * dy) < 15) {
        setPolygonClosed(true); // visually close; Save will use existing points
        return;
      }
    }
    setCurrentPolygon(prev => [...prev, { x, y }]);
  }

  async function handleSaveZone() {
    if (!editorCamera) { setZoneMsg('Select a camera first.'); return; }
    if (!zoneName.trim()) { setZoneMsg('Enter a zone name.'); return; }
    if (currentPolygon.length < 3) { setZoneMsg('Draw at least 3 points.'); return; }

    const cam = cameras.find(c => c.id === parseInt(editorCamera));
    if (!cam) { setZoneMsg('Camera not found.'); return; }

    // Convert display coords → original image coords
    const scaleX = imageNaturalSize.w / displaySize.w;
    const scaleY = imageNaturalSize.h / displaySize.h;
    const polygon = currentPolygon.map(p => [Math.round(p.x * scaleX), Math.round(p.y * scaleY)]);

    try {
      await createZone(cam.id, zoneName.trim(), cam.facility_id, polygon, isExclusion);
      setCurrentPolygon([]);
      setPolygonClosed(false);
      setIsExclusion(false);
      setZoneName('');
      setZoneMsg(isExclusion ? `Blackout zone "${zoneName}" saved!` : `Zone "${zoneName}" saved!`);
      await loadZones(cam.id);
    } catch (err) {
      setZoneMsg(`Save failed: ${err.message}`);
    }
  }

  async function handleDeleteZone(zoneId) {
    if (!window.confirm('Delete this zone?')) return;
    try {
      await deleteZone(zoneId);
      await loadZones(parseInt(editorCamera));
      setZoneMsg('Zone deleted.');
    } catch (err) {
      setZoneMsg(`Delete failed: ${err.message}`);
    }
  }

  // ── Detection ─────────────────────────────────────────────
  async function handleRunDetection() {
    if (!detCamera || !detImage) {
      setDetError('Select a camera and an image.');
      return;
    }
    setDetRunning(true);
    setDetError('');
    setDetResult(null);
    try {
      const result = await runDetection(parseInt(detCamera), detImage);
      setDetResult(result);
    } catch (err) {
      setDetError(`Detection failed: ${err.message}`);
    } finally {
      setDetRunning(false);
    }
  }

  // ── Render ────────────────────────────────────────────────
  return (
    <div className="acs-container">
      <div className="acs-header">
        <h2>Camera Setup</h2>
        <p className="acs-subtitle">Manage images, cameras, zones, and run detections</p>
      </div>

      <div className="acs-tabs">
        {TABS.map(t => (
          <button key={t} className={`acs-tab ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)}>
            {t}
          </button>
        ))}
      </div>

      {/* ── IMAGES TAB ── */}
      {tab === 'Images' && (
        <div className="acs-panel">
          {/* Download to Server (capture from RPI working dir) */}
          <div className="acs-capture-section">
            <h3 className="acs-section-title">Download to Server</h3>
            <p className="acs-hint">
              Grabs the next image from the camera feed, runs detection, archives it, then removes it from the working directory.
            </p>
            <div className="acs-form-row">
              <select className="acs-select" value={captureCamera} onChange={e => setCaptureCamera(e.target.value)}>
                <option value="">Select camera…</option>
                {cameras.map(c => (
                  <option key={c.id} value={c.id}>{c.location} (ID {c.id})</option>
                ))}
              </select>
              <button className="acs-btn primary" onClick={handleCaptureFrame} disabled={capturing}>
                {capturing ? 'Processing…' : 'Download to Server'}
              </button>
            </div>
            {captureError && <div className="acs-error">{captureError}</div>}
            {captureResult && (
              <div className={`acs-msg ${captureResult.warning ? 'error' : 'success'}`}>
                {captureResult.warning
                  ? <><strong>{captureResult.archived_filename}</strong> archived. {captureResult.warning}</>
                  : <>Captured <strong>{captureResult.archived_filename}</strong> — {captureResult.total_people} people detected.{captureResult.zones.map(z => (
                      <span key={z.zone_id} style={{ marginLeft: 8 }}>{z.zone_name}: {z.count}</span>
                    ))}</>
                }
              </div>
            )}
          </div>

          <hr style={{ margin: '16px 0', borderColor: '#333' }} />

          {/* Manual upload */}
          <div className="acs-actions">
            <button className="acs-btn secondary" onClick={() => fileInputRef.current.click()} disabled={uploading}>
              {uploading ? 'Uploading…' : '+ Upload Image Manually'}
            </button>
            <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleUpload} />
          </div>

          {imgError && <div className="acs-error">{imgError}</div>}
          {imgLoading && <div className="acs-loading">Loading images…</div>}
          {!imgLoading && images.length === 0 && (
            <div className="acs-empty">No archived images found. Use "Download to Server" or upload manually.</div>
          )}

          {/* Multi-select toolbar */}
          {images.length > 0 && !imgLoading && (
            <div className="acs-select-toolbar">
              <span className="acs-select-count">
                {selectedImages.size > 0 ? `${selectedImages.size} of ${images.length} selected` : `${images.length} image${images.length !== 1 ? 's' : ''}`}
              </span>
              <div className="acs-select-toolbar-actions">
                {selectedImages.size < images.length
                  ? <button className="acs-btn small secondary" onClick={selectAllImages}>Select All</button>
                  : <button className="acs-btn small secondary" onClick={clearSelection}>Deselect All</button>
                }
                {selectedImages.size > 0 && (
                  <button className="acs-btn small danger" onClick={handleDeleteSelected}>
                    Delete Selected ({selectedImages.size})
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Image grid — thumbnail + date */}
          <div className="acs-image-grid" style={{ marginTop: 8 }}>
            {images.map(img => {
              const d = img.modified_at ? new Date(img.modified_at * 1000) : null;
              const dateStr = d ? d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '';
              const timeStr = d ? d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) : '';
              const isSelected = selectedImages.has(img.filename);
              return (
                <div
                  key={img.filename}
                  className={`acs-image-card ${isSelected ? 'selected' : ''}`}
                >
                  <div className="acs-image-thumb" onClick={() => toggleSelectImage(img.filename)}>
                    <img src={imageUrl(img.filename)} alt={img.filename} loading="lazy" />
                    <div className={`acs-image-checkbox ${isSelected ? 'checked' : ''}`}>
                      {isSelected && <span className="acs-checkmark">✓</span>}
                    </div>
                  </div>
                  <div className="acs-image-info">
                    <span className="acs-image-name">{img.filename}</span>
                    {d && <span className="acs-image-date">{dateStr} · {timeStr}</span>}
                    <span className="acs-image-size">{(img.size_bytes / 1024).toFixed(1)} KB</span>
                  </div>
                  <div className="acs-image-actions">
                    <a href={imageUrl(img.filename)} download={img.filename} className="acs-btn small secondary">Download</a>
                    <button className="acs-btn small danger" onClick={() => handleDeleteImage(img.filename)}>Delete</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── CAMERAS TAB ── */}
      {tab === 'Cameras' && (
        <div className="acs-panel">
          <div className="acs-form-row">
            <input
              className="acs-input"
              placeholder="Camera location (e.g. GAC Main Floor)"
              value={newCamLocation}
              onChange={e => setNewCamLocation(e.target.value)}
            />
            <select className="acs-select" value={newCamFacility} onChange={e => setNewCamFacility(e.target.value)}>
              <option value="">Select facility…</option>
              {facilities.map(f => (
                <option key={f.id} value={f.id}>{f.display_name}</option>
              ))}
            </select>
            <button className="acs-btn primary" onClick={handleCreateCamera}>Add Camera</button>
            <button className="acs-btn secondary" onClick={() => setShowNewFacility(!showNewFacility)}>
              + New Facility
            </button>
          </div>

          {showNewFacility && (
            <div className="acs-form-row acs-indent">
              <input
                className="acs-input"
                placeholder="Facility name (e.g. Test Room)"
                value={newFacilityName}
                onChange={e => setNewFacilityName(e.target.value)}
              />
              <button className="acs-btn primary" onClick={handleCreateFacility}>Create Facility</button>
            </div>
          )}

          {camLoading && <div className="acs-loading">Loading cameras…</div>}
          <table className="acs-table">
            <thead>
              <tr>
                <th>ID</th><th>Location</th><th>Facility</th><th>Zones</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {cameras.map(cam => (
                <tr key={cam.id}>
                  <td>{cam.id}</td>
                  <td>{cam.location}</td>
                  <td>{cam.facility_name}</td>
                  <td>{cam.zones?.length || 0} zone(s)</td>
                  <td>
                    <button className="acs-btn small danger" onClick={() => handleDeleteCamera(cam.id)}>Delete</button>
                  </td>
                </tr>
              ))}
              {cameras.length === 0 && !camLoading && (
                <tr><td colSpan={5} style={{ textAlign: 'center', color: '#888' }}>No cameras. Add one above.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ── ZONE EDITOR TAB ── */}
      {tab === 'Zone Editor' && (
        <div className="acs-panel">
          <div className="acs-form-row">
            <select className="acs-select" value={editorCamera} onChange={e => setEditorCamera(e.target.value)}>
              <option value="">Select camera…</option>
              {cameras.map(c => (
                <option key={c.id} value={c.id}>{c.location} (ID {c.id})</option>
              ))}
            </select>
            <select className="acs-select" value={editorImage} onChange={e => setEditorImage(e.target.value)}>
              <option value="">Select image…</option>
              {images.map(img => (
                <option key={img.filename} value={img.filename}>{img.filename}</option>
              ))}
            </select>
          </div>

          {editorCamera && editorImage && (
            <>
              <div className="acs-zone-mode-row">
                <button
                  className={`acs-zone-mode-btn ${!isExclusion ? 'active-count' : ''}`}
                  onClick={() => { setIsExclusion(false); setCurrentPolygon([]); setPolygonClosed(false); }}
                >
                  Counting Zone
                </button>
                <button
                  className={`acs-zone-mode-btn ${isExclusion ? 'active-excl' : ''}`}
                  onClick={() => { setIsExclusion(true); setCurrentPolygon([]); setPolygonClosed(false); }}
                >
                  🚫 Blackout Zone
                </button>
              </div>
              <p className="acs-hint">
                {isExclusion
                  ? <><strong style={{color:'#ff6666'}}>Blackout mode:</strong> Draw a polygon over mirrors or areas to <strong>exclude</strong> from detection. Reflections inside will be ignored.</>
                  : <>Click to add polygon points. After 3+ points, click the <strong>orange dot</strong> (first point) to close the shape. Use <strong>Undo</strong> to remove the last point.</>
                }
              </p>
              <div className="acs-editor-wrap">
                <div className="acs-editor-canvas-container" style={{ width: displaySize.w, height: displaySize.h }}>
                  <img
                    ref={imgRef}
                    src={imageUrl(editorImage)}
                    alt="editor"
                    width={displaySize.w}
                    height={displaySize.h}
                    onLoad={handleImageLoad}
                    className="acs-editor-img"
                  />
                  <canvas
                    ref={canvasRef}
                    width={displaySize.w}
                    height={displaySize.h}
                    className="acs-editor-canvas"
                    onClick={handleCanvasClick}
                  />
                </div>
              </div>

              <div className="acs-form-row acs-zone-controls">
                <input
                  className="acs-input"
                  placeholder={isExclusion ? 'Blackout name (e.g. Mirror)' : 'Zone name (e.g. Cardio Area)'}
                  value={zoneName}
                  onChange={e => setZoneName(e.target.value)}
                />
                <span className="acs-point-count">
                  {currentPolygon.length} pt(s){polygonClosed ? ' — closed ✓' : currentPolygon.length >= 3 ? ' — click first point to close' : ''}
                </span>
                <button className="acs-btn secondary" onClick={() => {
                  if (polygonClosed) { setPolygonClosed(false); return; }
                  setCurrentPolygon(prev => prev.slice(0, -1));
                }} disabled={currentPolygon.length === 0}>Undo</button>
                <button className="acs-btn secondary" onClick={() => { setCurrentPolygon([]); setPolygonClosed(false); }}>Clear</button>
                <button className="acs-btn primary" onClick={handleSaveZone}>Save Zone</button>
              </div>
              {zoneMsg && <div className={`acs-msg ${zoneMsg.includes('failed') ? 'error' : 'success'}`}>{zoneMsg}</div>}
            </>
          )}

          {/* Saved zones list */}
          {editorCamera && savedZones.length > 0 && (
            <div className="acs-saved-zones">
              <h4>Saved Zones for this Camera</h4>
              <table className="acs-table">
                <thead>
                  <tr><th>ID</th><th>Name</th><th>Type</th><th>Points</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {savedZones.map(z => (
                    <tr key={z.zone_id}>
                      <td>{z.zone_id}</td>
                      <td>{z.zone_name}</td>
                      <td>
                        {z.is_exclusion
                          ? <span className="acs-badge excl">🚫 Blackout</span>
                          : <span className="acs-badge count">Counting</span>}
                      </td>
                      <td>{z.polygon?.length || 0} pts</td>
                      <td>
                        <button className="acs-btn small danger" onClick={() => handleDeleteZone(z.zone_id)}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── RUN DETECTION TAB ── */}
      {tab === 'Run Detection' && (
        <div className="acs-panel">
          <div className="acs-form-row">
            <select className="acs-select" value={detCamera} onChange={e => setDetCamera(e.target.value)}>
              <option value="">Select camera…</option>
              {cameras.map(c => (
                <option key={c.id} value={c.id}>{c.location} (ID {c.id}) — {c.zones?.length || 0} zones</option>
              ))}
            </select>
            <select className="acs-select" value={detImage} onChange={e => setDetImage(e.target.value)}>
              <option value="">Select image…</option>
              {images.map(img => (
                <option key={img.filename} value={img.filename}>{img.filename}</option>
              ))}
            </select>
            <button className="acs-btn primary" onClick={handleRunDetection} disabled={detRunning}>
              {detRunning ? 'Running YOLO…' : 'Run Detection'}
            </button>
          </div>

          {detError && <div className="acs-error">{detError}</div>}

          {detRunning && (
            <div className="acs-detection-running">
              <div className="acs-spinner" />
              <p>Running YOLO11s inference… this takes ~8 seconds on the ARM server.</p>
            </div>
          )}

          {detResult && (
            <div className="acs-detection-result">
              <div className="acs-result-header">
                <h3>Detection Complete</h3>
                <span className="acs-total-badge">{detResult.total_people} people total</span>
              </div>
              <p className="acs-result-meta">
                Camera {detResult.camera_id} · {detResult.image_filename} ·{' '}
                {new Date(detResult.timestamp + 'Z').toLocaleTimeString()}
              </p>
              {detResult.annotated_filename && (
                <div className="acs-annotated-img-wrap">
                  <img
                    src={imageUrl(detResult.annotated_filename)}
                    alt="Detection result with bounding boxes"
                    className="acs-annotated-img"
                  />
                </div>
              )}
              <div className="acs-zone-results">
                {detResult.zones.map(z => (
                  <div key={z.zone_id} className="acs-zone-result-card">
                    <div className="acs-zone-result-name">{z.zone_name}</div>
                    <div className="acs-zone-result-count">{z.count}</div>
                    <div className="acs-zone-result-label">people</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
